import React from "react";
import { GatsbyImage } from "gatsby-plugin-image";
import PropTypes from "prop-types";

// Components
import HeroChevronBody from "./hero-chevron-body";

// Styles
import styles from "./hero-chevron.module.scss";
import "./hero-chevron.bootstrap.scss";

const ChevronOverlay = (props) => {
  const chevronColorMap = {
    "bg-success": "chevron-green",
    "bg-green-60": "chevron-green",
    "bg-primary": "chevron-blue",
    "bg-blue-60": "chevron-blue",
    "bg-gray-20": "chevron-light-gray",
    "bg-info": "chevron-dark-green",
    "bg-commercial-green": "chevron-dark-green",
    "bg-dark-green-00": "chevron-very-dark-green",
    "bg-dark-green-01": "chevron-dark-green",
    "bg-warning": "chevron-yellow",
    "bg-yellow-00": "chevron-yellow",
    "bg-white": "chevron-white",
    "bg-kraken-ice-blue": "chevron-kraken-ice-blue",
    "bg-kraken-primary-blue": "chevron-kraken-primary-blue"
  };
  const chevronClass = chevronColorMap[props.chevronBgClass];
  return (
    <div className={`${props.chevronBgClass} ${styles.chevronContainer}`}>
      <div className={`${chevronClass} ${styles.chevronTop}`} />
      <div className={`${chevronClass} ${styles.chevronBottom}`} />
    </div>
  );
};

/**
 * @typedef {Object} SubComponentData
 * @property {string} id - id of the sub component content container to render.
 * @property {string} class - classes for the div that renders the text.
 * @property {string} url - in case the SubComponent is a button we can add a url or the `<Button>` props.
 * @property {string} text - required text to show in the sub component.
 */

/**
 * @typedef {Object} ComponentData
 * @property {string} id - id for the sub component to render
 * @property {SubComponentData} heading - optional renders the `<Heading>` component.
 * @property {SubComponentData} subheading - optional renders the `<Subheading>` component.
 * @property {SubComponentData} paragraph - optional renders the `<Paragraph>` component.
 * @property {SubComponentData} tertiaryText - optional renders the `<TertiaryText>` component.
 * @property {SubComponentData} list - optional renders the `<List>` component.
 * @property {SubComponentData} button - optional renders the `<Button>` component.
 */

/**
 * @typedef {Object} HeroBodyData
 * @property {ComponentData[]} components - array with the sub components to render
 */

/**
 * This is the component to render the Hero Chevron.
 *
 * @param {Object} props - The object containing props.
 * @param {string} props.id - required hero id applied to the `<div>` that wrap everything.
 * @param {string} props.altText - alt text used for all the images in their different sizes.
 * @param {string} props.chevronBgClass - classes applied to `<ChevronOverlay chevronBgClass={chevronBgClass} />`.
 * @param {HeroBodyData} props.heroBody - this will be true for 3rd Party injection of content. If true we render a `<div id="hero-content-placeholder"></div>` above the code of the components.
 * @param {GatsbyImageData} props.imgVariableXXS - XXS hero image data.
 * @param {GatsbyImageData} props.imgVariableXS - XS hero image data.
 * @param {GatsbyImageData} props.imgVariableSM - SM hero image data.
 * @param {GatsbyImageData} props.imgVariableMD - MD hero image data.
 * @param {GatsbyImageData} props.imgVariableLG - LG hero image data.
 * @param {GatsbyImageData} props.imgVariableXL - XL hero image data.
 * @param {GatsbyImageData} props.imgVariableXXL - XXL hero image data.
 * @returns {React.JSX.Element} Returns the JSX component to render
 */
const HeroChevron = ({
  id = "default-hero-chevron",
  altText = "WaFd Bank",
  chevronBgClass = "bg-success",
  heroBody = null,
  // Chevron Hero sizes for all breakpoints
  imgVariableXXS = null,
  imgVariableXS = null,
  imgVariableSM = null,
  imgVariableMD = null,
  imgVariableLG = null,
  imgVariableXL = null,
  imgVariableXXL = null
}) => {
  //console.log("HeroChevron props: ", props);

  /* ------------------------------------------------------------------------
  STANDARD HERO IMAGE SIZES
    XXS: <420px (320px min supported) - image size: 300px * 125px (new breakpoint)
    XS: 420-599px - image size: 420px * 200px (new breakpoint and image height)
    SM: 600-767px - image size: 500px * 250px
    MD: 768-991px - image size: 550px * 300px
    LG: 992-1199px - image size: 700px * 350px
    XL: 1200-1919px - image size: 1100px * 350px
    XXL: >=1920 (2500px max supported) - image size: 1400px * 350px
  ---------------------------------------------------------------------------
  NOTE: The standard naming conventions for chevron hero image fragments and image props are:
    "heroChevronImageFragmentXXS" --> "props.imgVariableXXS"  
    "heroChevronImageFragmentXS"  --> "props.imgVariableXS"
    "heroChevronImageFragmentSM"  --> "props.imgVariableSM"
    "heroChevronImageFragmentMD"  --> "props.imgVariableMD"
    "heroChevronImageFragmentLG"  --> "props.imgVariableLG"
    "heroChevronImageFragmentXL"  --> "props.imgVariableXL"
    "heroChevronImageFragmentXXL" --> "props.imgVariableXXL" 
  ----------------------------------------------------------------------------
  NOTE: the inline styles for "maxHeight" are necessary to keep the containing block 
  from overflowing below the hero image height. Some strange quirk with the positioning.
  ------------------------------------------------------------------------- */

  return (
    <div id={id} className={`hero hero-chevron ${styles.chevron}`}>
      {/* XXS */}
      {imgVariableXXS && (
        <div className="d-block d-xs420-none" style={{ maxHeight: "125px" }}>
          <ChevronOverlay chevronBgClass={chevronBgClass} />
          <div id="hero-chevron-image-container-xxs" className={styles.imageContainer}>
            <GatsbyImage loading="eager" image={imgVariableXXS} className={styles.chevronImage} alt={altText} />
          </div>
        </div>
      )}
      {/* XS */}
      {imgVariableXS && (
        <div className="d-none d-xs420-block d-sm-none" style={{ maxHeight: "200px" }}>
          <ChevronOverlay chevronBgClass={chevronBgClass} />
          <div id="hero-chevron-image-container-xs" className={styles.imageContainer}>
            <GatsbyImage loading="eager" image={imgVariableXS} className={styles.chevronImage} alt={altText} />
          </div>
        </div>
      )}
      {/* SM */}
      {imgVariableSM && (
        <div className="d-none d-sm-block d-md-none" style={{ maxHeight: "250px" }}>
          <ChevronOverlay chevronBgClass={chevronBgClass} />
          <div id="hero-chevron-image-container-sm" className={styles.imageContainer}>
            <GatsbyImage loading="eager" image={imgVariableSM} className={styles.chevronImage} alt={altText} />
          </div>
        </div>
      )}
      {/* MD */}
      {imgVariableMD && (
        <div className="d-none d-md-block d-lg-none" style={{ maxHeight: "300px" }}>
          <ChevronOverlay chevronBgClass={chevronBgClass} />
          <div id="hero-chevron-image-container-md" className={styles.imageContainer}>
            <GatsbyImage loading="eager" image={imgVariableMD} className={styles.chevronImage} alt={altText} />
          </div>
        </div>
      )}
      {/* LG */}
      {imgVariableLG && (
        <div className="d-none d-lg-block d-xl-none" style={{ maxHeight: "350px" }}>
          <ChevronOverlay chevronBgClass={chevronBgClass} />
          <div id="hero-chevron-image-container-lg" className={styles.imageContainer}>
            <GatsbyImage loading="eager" image={imgVariableLG} className={styles.chevronImage} alt={altText} />
          </div>
        </div>
      )}
      {/* XL */}
      {imgVariableXL && (
        <div className="d-none d-xl-block d-xxl-none" style={{ maxHeight: "350px" }}>
          <ChevronOverlay chevronBgClass={chevronBgClass} />
          <div id="hero-chevron-image-container-xl" className={styles.imageContainer}>
            <GatsbyImage loading="eager" image={imgVariableXL} className={styles.chevronImage} alt={altText} />
          </div>
        </div>
      )}
      {/* XXL */}
      {imgVariableXXL && (
        <div className="d-none d-xxl-block" style={{ maxHeight: "350px" }}>
          <ChevronOverlay chevronBgClass={chevronBgClass} />
          <div id="hero-chevron-image-container-xxl" className={styles.imageContainer}>
            <GatsbyImage loading="eager" image={imgVariableXXL} className={styles.chevronImage} alt={altText} />
          </div>
        </div>
      )}

      {/* HERO CHEVRON BODY */}
      {heroBody && <HeroChevronBody {...heroBody} />}
    </div>
  );
};
export default HeroChevron;

HeroChevron.propTypes = {
  id: PropTypes.string.isRequired,
  altText: PropTypes.string.isRequired,
  chevronBgClass: PropTypes.string,
  heroBody: PropTypes.shape({
    components: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        heading: PropTypes.shape({
          id: PropTypes.string,
          class: PropTypes.string,
          text: PropTypes.string.isRequired
        }),
        subheading: PropTypes.shape({
          id: PropTypes.string,
          class: PropTypes.string,
          text: PropTypes.string.isRequired
        }),
        paragraph: PropTypes.shape({
          id: PropTypes.string,
          class: PropTypes.string,
          text: PropTypes.string.isRequired
        }),
        tertiaryText: PropTypes.shape({
          id: PropTypes.string,
          class: PropTypes.string,
          text: PropTypes.string.isRequired
        }),
        list: PropTypes.shape({
          id: PropTypes.string,
          class: PropTypes.string,
          text: PropTypes.string.isRequired
        }),
        button: PropTypes.shape({
          id: PropTypes.string,
          class: PropTypes.string,
          text: PropTypes.string.isRequired
        })
      })
    )
  }),
  imgVariableXXS: PropTypes.object.isRequired,
  imgVariableXS: PropTypes.object.isRequired,
  imgVariableSM: PropTypes.object.isRequired,
  imgVariableMD: PropTypes.object.isRequired,
  imgVariableLG: PropTypes.object.isRequired,
  imgVariableXL: PropTypes.object.isRequired,
  imgVariableXXL: PropTypes.object.isRequired
};
